import { Spinner } from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@wc/domain/authn";
import CheckboxInput from "@wc/domain/components/win-card/checkbox-input";
import { formatDateInternal } from "@wc/domain/dates";
import {
  getWeekGoals,
  saveGoal,
  deleteGoal,
  isWeekLocked,
  useUserTeams,
} from "@wc/domain/goals";
import { useOrg } from "@wc/domain/organizations";
import {
  createGoalCheckedEvent,
  createGoalUncheckedEvent,
  createAllGoalsCompleteEvent,
} from "@wc/domain/teams/core/events-service";

let clientKeyCounter = 0;
/**
 * @returns {string} the client key
 */
function generateClientKey() {
  clientKeyCounter += 1;
  return `client-${clientKeyCounter}`;
}

/**
 * @typedef {object} LocalGoal
 * @property {string} [id] The goal id (undefined for new goals)
 * @property {string} clientKey Stable key for React rendering
 * @property {string} text The goal text
 * @property {boolean} completed Whether the goal is completed
 * @property {string} organizationId The organization this goal belongs to
 * @property {string} teamId The team this goal is associated with
 */

/**
 * @param {object} props The component props
 * @param {Date} props.date The date to reference
 * @param {boolean} props.isLocked Whether the week is locked
 * @param {(state: {hasSavedGoals: boolean, isLocked: boolean}) => void} [props.onStateChange] Callback when state changes
 * @returns {React.ReactElement} A list of checkbox inputs
 */
export default function InputList({ date, isLocked = false, onStateChange }) {
  const { t } = useTranslation();
  const authData = useAuth();
  const orgData = useOrg();
  const { teams, primaryTeamId, loading: teamsLoading } = useUserTeams();

  const [goals, setGoals] = React.useState([]);
  const [goalsLoading, setGoalsLoading] = React.useState(true);
  const [locked, setLocked] = React.useState(false);
  const goalRefs = React.useRef([]);
  const saveTimers = React.useRef({});
  const pendingSaves = React.useRef({});

  const weekOf = formatDateInternal(date);
  const userId = authData.isLoggedIn() ? authData.getCurrentUser().id : null;
  const organizationId = orgData?.org?.id;

  React.useEffect(() => {
    setLocked(isLocked);
  }, [isLocked]);

  React.useEffect(() => {
    const timers = saveTimers.current;
    const pending = pendingSaves.current;
    return () => {
      // Cancel debounce timers but flush any pending saves immediately
      Object.values(timers).forEach(clearTimeout);
      Object.entries(pending).forEach(([key, { goalData, uid, week }]) => {
        if (uid && goalData.text?.trim()) {
          saveGoal(goalData, uid, week).catch((err) =>
            console.error("Failed to flush goal on unmount:", err),
          );
        }
        delete pending[key];
      });
    };
  }, []);

  React.useEffect(() => {
    const loadGoals = async () => {
      if (!userId || !organizationId || teamsLoading) return;

      try {
        setGoalsLoading(true);
        const [fetchedGoals, weekLocked] = await Promise.all([
          getWeekGoals(userId, weekOf, organizationId),
          isWeekLocked(userId, organizationId, weekOf).catch((err) => {
            console.error("Failed to check lock status:", err);
            return false;
          }),
        ]);

        setLocked(weekLocked);

        if (fetchedGoals.length === 0) {
          setGoals([
            {
              completed: false,
              text: "",
              organizationId,
              teamId: primaryTeamId,
              clientKey: generateClientKey(),
            },
          ]);
        } else {
          setGoals(
            fetchedGoals.map((g) => ({ ...g, clientKey: generateClientKey() })),
          );
        }
      } catch (error) {
        console.error("Failed to load goals:", error);
        setGoals([
          {
            completed: false,
            text: "",
            organizationId,
            teamId: primaryTeamId,
            clientKey: generateClientKey(),
          },
        ]);
      } finally {
        setGoalsLoading(false);
      }
    };

    loadGoals();
  }, [userId, weekOf, organizationId, teamsLoading, primaryTeamId]);

  const hasSavedGoals = goals.some((g) => g.id);

  React.useEffect(() => {
    if (onStateChange && !goalsLoading) {
      onStateChange({ hasSavedGoals, isLocked: locked });
    }
  }, [hasSavedGoals, locked, goalsLoading, onStateChange]);

  /**
   * @param {LocalGoal} goalData The goal to persist
   * @param {string} clientKey The stable client key for this goal
   */
  const persistGoal = React.useCallback(
    (goalData, clientKey) => {
      if (!userId || !goalData.text.trim()) return;

      if (saveTimers.current[clientKey]) {
        clearTimeout(saveTimers.current[clientKey]);
      }

      // Track pending save data so it can be flushed on unmount
      pendingSaves.current[clientKey] = {
        goalData,
        uid: userId,
        week: weekOf,
      };

      saveTimers.current[clientKey] = setTimeout(async () => {
        delete pendingSaves.current[clientKey];
        try {
          // Find the existing goal before saving to compare completion status
          const existingGoal = goals.find((g) => g.clientKey === clientKey);
          const wasCompleted = existingGoal?.completed || false;
          const isCompleted = goalData.completed;

          const savedId = await saveGoal(goalData, userId, weekOf);
          setGoals((prev) =>
            prev.map((g) =>
              g.clientKey === clientKey ? { ...g, id: savedId } : g,
            ),
          );

          // Create events if completion status changed and goal has a team
          if (wasCompleted !== isCompleted && goalData.teamId) {
            try {
              if (isCompleted) {
                await createGoalCheckedEvent(
                  goalData.teamId,
                  userId,
                  savedId || goalData.id,
                  goalData.text,
                  weekOf,
                );

                const allGoals = await getWeekGoals(
                  userId,
                  weekOf,
                  organizationId,
                );
                if (allGoals.length > 0 && allGoals.every((g) => g.completed)) {
                  await createAllGoalsCompleteEvent(
                    goalData.teamId,
                    userId,
                    weekOf,
                  );
                }
              } else {
                // Goal was unchecked
                await createGoalUncheckedEvent(
                  goalData.teamId,
                  userId,
                  savedId || goalData.id,
                  goalData.text,
                  weekOf,
                );
              }
            } catch (error) {
              console.error("Failed to create goal completion event:", error);
              // Don't fail the save operation if event creation fails
            }
          }
        } catch (error) {
          console.error("Failed to save goal:", error);
        }
      }, 500);
    },
    [userId, weekOf, organizationId, goals],
  );

  /**
   * @param {boolean} checked Whether the user checked the goal or not
   * @param {string} value The new value of the input
   * @param {number} index The index of the changed input
   */
  const handleChange = (checked, value, index) => {
    const goal = goals[index];
    const textChanged = value !== goal.text;

    if (locked && textChanged) {
      return;
    }

    const updatedGoal = {
      ...goal,
      completed: checked,
      text: locked ? goal.text : value,
    };

    setGoals((prev) => prev.map((g, i) => (i === index ? updatedGoal : g)));

    if (updatedGoal.text.trim()) {
      persistGoal(updatedGoal, goal.clientKey);
    }
  };

  /**
   * @param {string} teamId The new team id
   * @param {number} index The index of the changed input
   */
  const handleTeamChange = (teamId, index) => {
    if (locked) return;

    const goal = goals[index];
    const updatedGoal = { ...goal, teamId };

    setGoals((prev) => prev.map((g, i) => (i === index ? updatedGoal : g)));

    if (updatedGoal.text?.trim()) {
      persistGoal(updatedGoal, goal.clientKey);
    }
  };

  const focusInput = React.useCallback(
    (index) => {
      if (index >= 0 && index < goals.length) {
        setTimeout(() => {
          const ref = goalRefs.current[index]?.current;
          const inputElement = ref?.querySelector("input") || ref?.base || ref;

          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 10);
      }
    },
    [goals.length],
  );

  /**
   * @param {number} index The index of the input where Enter was pressed
   */
  const handleEnter = (index) => {
    if (goals.length >= 5 || index < goals.length - 1) {
      focusInput(index + 1);
    } else if (goals.length < 5 && !locked) {
      setGoals((prev) => [
        ...prev,
        {
          completed: false,
          text: "",
          organizationId,
          teamId: primaryTeamId,
          clientKey: generateClientKey(),
        },
      ]);
    }
  };

  /**
   * @param {number} index The index of the current input
   * @param {boolean} isShiftKey Whether Shift key was pressed with Tab
   */
  const handleTab = (index, isShiftKey) => {
    const nextIndex = isShiftKey ? index - 1 : index + 1;
    if (nextIndex >= 0 && nextIndex < goals.length) {
      setTimeout(() => {
        focusInput(nextIndex);
      }, 0);
    }
  };

  /**
   * @param {number} index The index of the goal to delete
   */
  const handleDelete = async (index) => {
    const goal = goals[index];

    // Soft-delete in Firestore if the goal has been persisted
    if (goal.id && userId) {
      try {
        await deleteGoal(goal.id, userId);
      } catch (error) {
        console.error("Failed to delete goal:", error);
        return;
      }
    }

    // Clear any pending save timer for this goal
    if (saveTimers.current[goal.clientKey]) {
      clearTimeout(saveTimers.current[goal.clientKey]);
      delete saveTimers.current[goal.clientKey];
    }

    setGoals((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // If removing the last goal, replace with an empty row
      if (updated.length === 0) {
        return [
          {
            completed: false,
            text: "",
            organizationId,
            teamId: primaryTeamId,
            clientKey: generateClientKey(),
          },
        ];
      }
      return updated;
    });
  };

  React.useEffect(() => {
    goalRefs.current = goalRefs.current.slice(0, goals.length);
    while (goalRefs.current.length < goals.length) {
      goalRefs.current.push({ current: null });
    }

    const lastIndex = goals.length - 1;
    if (lastIndex >= 0 && !goalsLoading) {
      focusInput(lastIndex);
    }
  }, [goals.length, focusInput, goalsLoading]);

  if (teamsLoading || goalsLoading || !organizationId) {
    return (
      <div className="flex justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full p-4">
      <div className="mb-4 flex flex-col items-center justify-between gap-4">
        {goals.map((goal, index) => {
          const placeholder =
            index === 0 ? t("first-placeholder") : t("nth-placeholder");

          const isLast = index === goals.length - 1;
          const isFinal = goals.length === 5;

          return (
            <CheckboxInput
              inputRef={goalRefs.current[index]}
              key={goal.clientKey}
              index={index}
              isLast={isLast}
              isFinal={isFinal}
              checked={goal.completed}
              valueText={goal.text}
              placeholderText={placeholder}
              teamId={goal.teamId}
              teams={teams}
              isLocked={locked}
              onChange={handleChange}
              onEnter={handleEnter}
              onTab={handleTab}
              onTeamChange={handleTeamChange}
              onDelete={handleDelete}
            />
          );
        })}
      </div>
    </div>
  );
}
